import React, { useEffect, useMemo, useState } from "react";
import "./AssetManagementDashboard.css";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  FiBox,
  FiCheckCircle,
  FiPlayCircle,
  FiArchive,
  FiTool,
  FiFileText,
  FiUsers,
  FiShoppingCart,
  FiDollarSign,
  FiClock,
  FiAlertTriangle,
  FiTrendingUp,
  FiBarChart2,
  FiPieChart,
  FiRefreshCw,
  FiPackage,
} from "react-icons/fi";

const COLORS = ["#2563eb", "#059669", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#db2777"];

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => safeNumber(value).toLocaleString("en-IN");

const formatCurrency = (value) =>
  `₹${safeNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const getPercentage = (part, total) => {
  const p = safeNumber(part);
  const t = safeNumber(total);

  if (!t) return 0;

  return Math.min(100, Math.max(0, Math.round((p / t) * 100)));
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="amdb-tooltip">
      {label && <div className="amdb-tooltip-label">{label}</div>}
      {payload.map((item, index) => (
        <div className="amdb-tooltip-row" key={index}>
          <span
            className="amdb-tooltip-dot"
            style={{ background: item.color || item.fill }}
          />
          <span>{item.name || item.dataKey}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, helper, tone = "blue" }) => (
  <div className={`amdb-kpi-card amdb-tone-${tone}`}>
    <div className="amdb-kpi-icon">
      <Icon size={21} />
    </div>
    <div className="amdb-kpi-content">
      <div className="amdb-kpi-label">{label}</div>
      <div className="amdb-kpi-value">{value}</div>
      {helper && <div className="amdb-kpi-helper">{helper}</div>}
    </div>
  </div>
);

const InsightCard = ({ icon: Icon, title, value, note, tone = "blue", progress }) => (
  <div className={`amdb-insight-card amdb-tone-${tone}`}>
    <div className="amdb-insight-top">
      <div className="amdb-insight-icon">
        <Icon size={19} />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
    </div>
    <div className="amdb-insight-value">{value}</div>
    {typeof progress === "number" && (
      <div className="amdb-progress">
        <span style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
      </div>
    )}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="amdb-section-header">
    <div className="amdb-section-icon">
      <Icon size={18} />
    </div>
    <div>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  </div>
);

const SnapshotRow = ({ label, value, pill, tone = "blue" }) => (
  <div className="amdb-snapshot-row">
    <span>{label}</span>
    <strong>{value}</strong>
    {pill && <span className={`amdb-pill amdb-pill-${tone}`}>{pill}</span>}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="amdb-empty-state">
    <FiCheckCircle size={28} />
    <span>{message}</span>
  </div>
);

const AssetManagementDashboard = () => {
  const { Get } = useApi();

  const [loading, setLoading] = useState(false);
  const [allocations, setAllocations] = useState({});
  const [requests, setRequests] = useState({});
  const [maintenanceCost, setMaintenanceCost] = useState({});
  const [chartData, setChartData] = useState([]);
  const [assetSummaryTable, setAssetSummaryTable] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [categoryPieData, setCategoryPieData] = useState([]);

  const getNoDataMessage = (data) => {
    return data?.assets?.total === 0
      ? "No assets found. Add your first asset to get started."
      : null;
  };

  const fetchAssetSummary = async () => {
    const data = await Get("assetSummary");
    const summary = Array.isArray(data) ? data : [];

    const transformed = summary.map((item) => ({
      name: item.category_name,
      available: safeNumber(item.available),
      in_use: safeNumber(item.in_use),
    }));

    setChartData(transformed);
    setAssetSummaryTable(summary);
  };

  const fetchDashboardData = async () => {
    const data = await Get("assetManagementDashboard");

    setDashboardData(data);
    setCategoryPieData(
      (data?.category_wise_assets || []).map((item) => ({
        name: item.category_name,
        value: safeNumber(item.total),
      }))
    );
    setAllocations(data?.allocations || {});
    setRequests(data?.requests || {});
    setMaintenanceCost(data?.maintenance_cost || {});
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchAssetSummary(), fetchDashboardData()])
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  }, []);

  const assets = dashboardData?.assets || {};
  const rfq = dashboardData?.rfq || {};
  const rfqVendor = dashboardData?.rfq_vendor || {};
  const purchaseOrders = dashboardData?.purchase_orders || {};
  const purchaseOrderItems = dashboardData?.purchase_order_items || {};

  const totalAssets = safeNumber(assets.total);
  const availableAssets = safeNumber(assets.available);
  const inUseAssets = safeNumber(assets.in_use);
  const maintenanceAssets = safeNumber(assets.under_maintenance);
  const retiredAssets = safeNumber(assets.retired);

  const utilizationRate = getPercentage(inUseAssets, totalAssets);
  const availabilityRate = getPercentage(availableAssets, totalAssets);
  const maintenanceRate = getPercentage(maintenanceAssets, totalAssets);
  const poDeliveryRate = getPercentage(purchaseOrders.delivered, purchaseOrders.total);
  const rfqClosureRate = getPercentage(rfq.closed, rfq.total);
  const requestApprovalRate = getPercentage(requests.approved, safeNumber(requests.approved) + safeNumber(requests.rejected) + safeNumber(requests.pending));

  const totalMaintenanceCost = safeNumber(maintenanceCost.total_cost);
  const unpaidMaintenanceCost = safeNumber(maintenanceCost.unpaid_cost);

  const assetHealthTone = maintenanceRate >= 20 ? "red" : maintenanceRate >= 10 ? "amber" : "green";

  const currentDateLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const sortedAssetSummaryTable = useMemo(
    () =>
      [...assetSummaryTable].sort(
        (a, b) => safeNumber(b.total) - safeNumber(a.total)
      ),
    [assetSummaryTable]
  );

  return (
    <div className="responsive-box amdb-page">
      <style>{dashboardStyles}</style>

      {loading && (
        <div className="amdb-loading">
          <div className="amdb-spinner" />
          <div>
            <h3>Loading Asset Dashboard</h3>
            <p>Preparing asset health, allocation, procurement and maintenance insights.</p>
          </div>
        </div>
      )}

      {!loading && dashboardData && getNoDataMessage(dashboardData) && (
        <div className="amdb-empty-page">
          <FiBox size={36} />
          <h2>No Assets Found</h2>
          <p>{getNoDataMessage(dashboardData)}</p>
        </div>
      )}

      {!loading && dashboardData && !getNoDataMessage(dashboardData) && (
        <div className="amdb-shell">
          <section className="amdb-hero">
            <div>
              <div className="amdb-kicker">
                <span className="amdb-kicker-dot" />
                Asset Business View
              </div>
              <h1>Asset Management Dashboard</h1>
              <p>
                Simple executive view for asset availability, utilization, maintenance, requests and procurement activity.
              </p>
              <div className="amdb-date-pill">{currentDateLabel}</div>
            </div>

            <div className="amdb-hero-kpis">
              <KpiCard
                icon={FiBox}
                label="Total Assets"
                value={formatCount(totalAssets)}
                helper="All active asset records"
                tone="blue"
              />
              <KpiCard
                icon={FiPlayCircle}
                label="In Use"
                value={`${formatCount(inUseAssets)} (${utilizationRate}%)`}
                helper="Current asset utilization"
                tone="purple"
              />
              <KpiCard
                icon={FiTool}
                label="Maintenance"
                value={`${formatCount(maintenanceAssets)} (${maintenanceRate}%)`}
                helper="Assets needing attention"
                tone={assetHealthTone}
              />
            </div>
          </section>

          <section className="amdb-insight-grid">
            <InsightCard
              icon={FiCheckCircle}
              title="Availability"
              value={`${availabilityRate}%`}
              note={`${formatCount(availableAssets)} assets ready to allocate`}
              progress={availabilityRate}
              tone="green"
            />
            <InsightCard
              icon={FiTrendingUp}
              title="Utilization"
              value={`${utilizationRate}%`}
              note={`${formatCount(inUseAssets)} assets currently in use`}
              progress={utilizationRate}
              tone="blue"
            />
            <InsightCard
              icon={FiAlertTriangle}
              title="Maintenance Risk"
              value={`${maintenanceRate}%`}
              note={`${formatCount(maintenanceAssets)} assets under maintenance`}
              progress={maintenanceRate}
              tone={assetHealthTone}
            />
            <InsightCard
              icon={FiShoppingCart}
              title="PO Delivery"
              value={`${poDeliveryRate}%`}
              note={`${formatCount(purchaseOrders.delivered)} delivered out of ${formatCount(purchaseOrders.total)} POs`}
              progress={poDeliveryRate}
              tone="purple"
            />
          </section>

          <section className="amdb-focus-grid">
            <div className="amdb-panel">
              <SectionHeader
                icon={FiRefreshCw}
                title="Operational Snapshot"
                subtitle="Allocation, request and maintenance view for quick decisions"
              />

              <div className="amdb-snapshot-list">
                <SnapshotRow
                  label="Active Allocations"
                  value={formatCount(allocations.active)}
                  pill={`${formatCount(allocations.returned)} Returned`}
                  tone="blue"
                />
                <SnapshotRow
                  label="Pending Requests"
                  value={formatCount(requests.pending)}
                  pill={`${requestApprovalRate}% Approved`}
                  tone={requests.pending > 0 ? "amber" : "green"}
                />
                <SnapshotRow
                  label="Maintenance Cost"
                  value={formatCurrency(totalMaintenanceCost)}
                  pill={`${formatCurrency(unpaidMaintenanceCost)} Unpaid`}
                  tone={unpaidMaintenanceCost > 0 ? "red" : "green"}
                />
                <SnapshotRow
                  label="Retired Assets"
                  value={formatCount(retiredAssets)}
                  pill="Archived"
                  tone="slate"
                />
              </div>
            </div>

            <div className="amdb-panel">
              <SectionHeader
                icon={FiFileText}
                title="Procurement Snapshot"
                subtitle="RFQ and purchase order status without operational clutter"
              />

              <div className="amdb-snapshot-list">
                <SnapshotRow
                  label="RFQs"
                  value={formatCount(rfq.total)}
                  pill={`${rfqClosureRate}% Closed`}
                  tone="blue"
                />
                <SnapshotRow
                  label="RFQ Vendors"
                  value={formatCount(rfqVendor.rfq_sent)}
                  pill={`${formatCount(rfqVendor.approved)} Approved`}
                  tone="purple"
                />
                <SnapshotRow
                  label="Purchase Orders"
                  value={formatCount(purchaseOrders.total)}
                  pill={`${formatCount(purchaseOrders.delivered)} Delivered`}
                  tone="green"
                />
                <SnapshotRow
                  label="PO Items"
                  value={formatCount(purchaseOrderItems.total_items)}
                  pill={`${formatCount(purchaseOrderItems.total_ordered_quantity)} Qty`}
                  tone="amber"
                />
              </div>
            </div>
          </section>

          <section className="amdb-chart-grid">
            <div className="amdb-panel">
              <SectionHeader
                icon={FiPieChart}
                title="Category Distribution"
                subtitle="Asset count by category"
              />

              <div className="amdb-chart-box">
                {categoryPieData.length === 0 ? (
                  <EmptyState message="No category distribution available" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryPieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={52}
                        outerRadius={90}
                        paddingAngle={4}
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {categoryPieData.map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="amdb-panel">
              <SectionHeader
                icon={FiBarChart2}
                title="Availability by Category"
                subtitle="Available vs in-use assets"
              />

              <div className="amdb-chart-box">
                {chartData.length === 0 ? (
                  <EmptyState message="No category summary available" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} barGap={8}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar
                        dataKey="available"
                        name="Available"
                        fill="#059669"
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar
                        dataKey="in_use"
                        name="In Use"
                        fill="#2563eb"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </section>

          <section className="amdb-panel">
            <SectionHeader
              icon={FiPackage}
              title="Category Asset Summary"
              subtitle="Detailed category-level availability and maintenance status"
            />

            {sortedAssetSummaryTable.length === 0 ? (
              <EmptyState message="No asset summary available" />
            ) : (
              <div className="amdb-table-wrap">
                <table className="amdb-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Total</th>
                      <th>In Use</th>
                      <th>Under Maintenance</th>
                      <th>Available</th>
                      <th>Availability</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAssetSummaryTable.map((item) => {
                      const rowTotal = safeNumber(item.total);
                      const rowAvailable = safeNumber(item.available);
                      const rowAvailability = getPercentage(rowAvailable, rowTotal);

                      return (
                        <tr key={item.category_id || item.category_name}>
                          <td>
                            <strong className="amdb-table-title">{item.category_name}</strong>
                          </td>
                          <td>{formatCount(item.total)}</td>
                          <td>
                            <span className="amdb-pill amdb-pill-blue">
                              {formatCount(item.in_use)}
                            </span>
                          </td>
                          <td>
                            <span className="amdb-pill amdb-pill-red">
                              {formatCount(item.maintenance)}
                            </span>
                          </td>
                          <td>
                            <span className="amdb-pill amdb-pill-green">
                              {formatCount(item.available)}
                            </span>
                          </td>
                          <td>
                            <div className="amdb-progress amdb-progress-table">
                              <span style={{ width: `${rowAvailability}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default AssetManagementDashboard;

const dashboardStyles = `
  .amdb-page {
    min-height: 100vh;
    padding: 10px 12px 28px;
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 28%),
      radial-gradient(circle at top right, rgba(16, 185, 129, 0.07), transparent 28%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    color: #0f172a;
  }

  .amdb-shell {
    max-width: 1500px;
    margin: 0 auto;
  }

  .amdb-hero {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(440px, 0.85fr);
    gap: 18px;
    align-items: center;
    padding: 22px;
    margin-bottom: 14px;
    border-radius: 28px;
    color: #ffffff;
    background: linear-gradient(135deg, #020617 0%, #111827 56%, #075985 100%);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
  }

  .amdb-hero::after {
    content: "";
    position: absolute;
    width: 320px;
    height: 320px;
    right: -120px;
    top: -160px;
    border-radius: 999px;
    background: rgba(56, 189, 248, 0.16);
  }

  .amdb-hero > * {
    position: relative;
    z-index: 1;
  }

  .amdb-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 11px;
    border-radius: 999px;
    margin-bottom: 10px;
    font-size: 11px;
    font-weight: 950;
    color: #e0f2fe;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.22);
  }

  .amdb-kicker-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.16);
  }

  .amdb-hero h1 {
    margin: 0;
    font-size: 31px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.045em;
  }

  .amdb-hero p {
    max-width: 720px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.80);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.6;
  }

  .amdb-date-pill {
    display: inline-flex;
    margin-top: 13px;
    padding: 7px 11px;
    border-radius: 999px;
    color: #bfdbfe;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 900;
  }

  .amdb-hero-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .amdb-kpi-card,
  .amdb-insight-card,
  .amdb-panel {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid #dbe3ec;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
  }

  .amdb-kpi-card {
    display: flex;
    align-items: center;
    gap: 13px;
    min-height: 82px;
    padding: 13px;
    border-radius: 20px;
  }

  .amdb-kpi-icon,
  .amdb-insight-icon,
  .amdb-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--amdb-accent);
    background: var(--amdb-soft);
    border: 1px solid var(--amdb-border);
    flex-shrink: 0;
  }

  .amdb-kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 15px;
  }

  .amdb-kpi-label {
    color: #475569;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .amdb-kpi-value {
    margin-top: 2px;
    color: #0f172a;
    font-size: 21px;
    font-weight: 950;
    letter-spacing: -0.035em;
  }

  .amdb-kpi-helper {
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .amdb-insight-grid,
  .amdb-focus-grid,
  .amdb-chart-grid {
    display: grid;
    gap: 14px;
    margin-bottom: 14px;
  }

  .amdb-insight-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .amdb-focus-grid,
  .amdb-chart-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .amdb-insight-card {
    border-radius: 22px;
    padding: 15px;
  }

  .amdb-insight-top {
    display: flex;
    gap: 11px;
    align-items: flex-start;
  }

  .amdb-insight-icon {
    width: 42px;
    height: 42px;
    border-radius: 15px;
  }

  .amdb-insight-card h3 {
    margin: 0;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .amdb-insight-card p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.45;
  }

  .amdb-insight-value {
    margin-top: 14px;
    color: #0f172a;
    font-size: 26px;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .amdb-panel {
    border-radius: 24px;
    padding: 16px;
    margin-bottom: 14px;
  }

  .amdb-section-header {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 14px;
  }

  .amdb-section-icon {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    --amdb-accent: #0f172a;
    --amdb-soft: #f8fafc;
    --amdb-border: #dbe3ec;
  }

  .amdb-section-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -0.025em;
  }

  .amdb-section-header p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .amdb-snapshot-list {
    display: grid;
    gap: 10px;
  }

  .amdb-snapshot-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-radius: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }

  .amdb-snapshot-row span:first-child {
    color: #475569;
    font-size: 12px;
    font-weight: 850;
  }

  .amdb-snapshot-row strong {
    color: #0f172a;
    font-size: 15px;
    font-weight: 950;
  }

  .amdb-chart-box {
    height: 310px;
    min-height: 310px;
  }

  .amdb-progress {
    width: 100%;
    height: 8px;
    margin-top: 10px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
  }

  .amdb-progress-table {
    min-width: 140px;
    margin-top: 0;
  }

  .amdb-progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--amdb-accent, #2563eb);
  }

  .amdb-table-wrap {
    width: 100%;
    overflow-x: auto;
  }

  .amdb-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 7px;
    min-width: 760px;
  }

  .amdb-table th {
    padding: 10px 12px;
    color: #64748b;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 11px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .amdb-table th:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .amdb-table th:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .amdb-table td {
    padding: 11px 12px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
    font-size: 13px;
    font-weight: 750;
    vertical-align: middle;
  }

  .amdb-table td:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .amdb-table td:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .amdb-table-title {
    color: #0f172a;
    font-weight: 950;
  }

  .amdb-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    padding: 5px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .amdb-pill-blue {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .amdb-pill-green {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  }

  .amdb-pill-purple {
    background: #f3e8ff;
    color: #7c3aed;
    border: 1px solid #ddd6fe;
  }

  .amdb-pill-amber {
    background: #fffbeb;
    color: #b45309;
    border: 1px solid #fde68a;
  }

  .amdb-pill-red {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }

  .amdb-pill-slate {
    background: #f8fafc;
    color: #475569;
    border: 1px solid #cbd5e1;
  }

  .amdb-empty-state {
    min-height: 190px;
    border: 1px dashed #cbd5e1;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #64748b;
    font-size: 13px;
    font-weight: 850;
    background: #f8fafc;
  }

  .amdb-empty-page {
    min-height: 430px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    color: #0f172a;
  }

  .amdb-empty-page svg {
    color: #2563eb;
    margin-bottom: 14px;
  }

  .amdb-empty-page h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 950;
  }

  .amdb-empty-page p {
    max-width: 480px;
    margin: 8px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .amdb-tooltip {
    background: #ffffff;
    border: 1px solid #dbe3ec;
    border-radius: 14px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.16);
    padding: 9px 10px;
    min-width: 130px;
  }

  .amdb-tooltip-label {
    color: #0f172a;
    font-size: 12px;
    font-weight: 950;
    margin-bottom: 6px;
  }

  .amdb-tooltip-row {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .amdb-tooltip-row strong {
    margin-left: auto;
    color: #0f172a;
  }

  .amdb-tooltip-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
  }

  .amdb-tone-blue {
    --amdb-accent: #2563eb;
    --amdb-soft: #eff6ff;
    --amdb-border: #bfdbfe;
  }

  .amdb-tone-green {
    --amdb-accent: #059669;
    --amdb-soft: #ecfdf5;
    --amdb-border: #a7f3d0;
  }

  .amdb-tone-purple {
    --amdb-accent: #7c3aed;
    --amdb-soft: #f3e8ff;
    --amdb-border: #ddd6fe;
  }

  .amdb-tone-red {
    --amdb-accent: #dc2626;
    --amdb-soft: #fef2f2;
    --amdb-border: #fecaca;
  }

  .amdb-tone-amber {
    --amdb-accent: #d97706;
    --amdb-soft: #fffbeb;
    --amdb-border: #fde68a;
  }

  .amdb-tone-slate {
    --amdb-accent: #475569;
    --amdb-soft: #f8fafc;
    --amdb-border: #cbd5e1;
  }

  .amdb-loading {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    color: #0f172a;
  }

  .amdb-loading h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 950;
  }

  .amdb-loading p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .amdb-spinner {
    width: 54px;
    height: 54px;
    border-radius: 999px;
    border: 5px solid #dbeafe;
    border-top-color: #2563eb;
    animation: amdb-spin 0.8s linear infinite;
  }

  @keyframes amdb-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 1200px) {
    .amdb-hero {
      grid-template-columns: 1fr;
    }

    .amdb-insight-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .amdb-focus-grid,
    .amdb-chart-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .amdb-page {
      padding: 8px 8px 22px;
    }

    .amdb-hero {
      border-radius: 22px;
      padding: 18px;
    }

    .amdb-hero h1 {
      font-size: 25px;
    }

    .amdb-insight-grid {
      grid-template-columns: 1fr;
    }

    .amdb-panel,
    .amdb-insight-card {
      border-radius: 20px;
      padding: 14px;
    }

    .amdb-snapshot-row {
      grid-template-columns: 1fr;
      align-items: flex-start;
    }

    .amdb-chart-box {
      height: 280px;
      min-height: 280px;
    }
  }
`;
